if (!customElements.get('proof-numbers')) {
  class ProofNumbers extends HTMLElement {
    connectedCallback() {
      this._waitForGsap();
    }

    _waitForGsap() {
      if (typeof gsap !== 'undefined' && typeof Observer !== 'undefined') {
        this._init();
      } else {
        requestAnimationFrame(() => this._waitForGsap());
      }
    }

    _init() {
      gsap.registerPlugin(Observer);

      this.cards = [...this.querySelectorAll('[data-stat-card]')];
      this.track = this.querySelector('[data-mobile-track]');
      this.slides = [...this.querySelectorAll('[data-mobile-slide]')];
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.slideCount = this.slides.length;
      this.current = 0;
      this.animating = false;

      this._observeHeading();
      this._observeEntry();

      if (this.track && this.slideCount > 0 && window.innerWidth < 1024) {
        this._initCarousel();
      }
    }

    _observeHeading() {
      var self = this;
      var heading = this.querySelector('[data-proof-heading]');
      if (!heading) return;
      var obs = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          self._animateHeading();
        }
      }, { threshold: 0.5, rootMargin: '0px 0px -10% 0px' });
      obs.observe(heading);
    }

    _animateHeading() {
      var self = this;
      var words = this.querySelectorAll('[data-proof-word]');
      var stagger = 60;
      var wordDuration = 210;
      var totalDuration = (words.length - 1) * stagger + wordDuration;

      words.forEach(function (word, i) {
        var blur = Math.min((i + 1) * 1.5, 10);
        var anim = word.animate(
          [
            { opacity: 0, filter: 'blur(' + blur + 'px)', transform: 'translateY(6px)' },
            { opacity: 1, filter: 'blur(0px)', transform: 'translateY(0)' }
          ],
          { duration: wordDuration, delay: i * stagger, easing: 'ease-out', fill: 'forwards' }
        );
        anim.onfinish = function () {
          word.style.opacity = '1';
          word.style.filter = 'none';
          word.style.transform = 'none';
          anim.cancel();
        };
      });

      setTimeout(function () { self._animateDesc(); }, totalDuration);
    }

    _animateDesc() {
      var desc = this.querySelector('[data-proof-desc]');
      if (!desc) return;
      desc.animate(
        [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 0.8, transform: 'translateY(0)' }
        ],
        { duration: 200, easing: 'ease-out', fill: 'forwards' }
      );
    }

    _observeEntry() {
      const isMobile = window.innerWidth < 1024;
      const staticCards = [];
      const carouselCards = [];

      this.cards.forEach((card) => {
        if (isMobile && card.closest('[data-mobile-carousel]')) {
          carouselCards.push(card);
        } else {
          staticCards.push(card);
          card.style.opacity = '0';
          card.style.transform = 'translateY(50px)';
        }
      });

      const grid = this.querySelector('[data-cards-grid]');
      if (grid && staticCards.length > 0) {
        const gridObserver = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            staticCards.forEach((card, i) => {
              setTimeout(() => {
                this._animateCardIn(card);
                this._animateChartsInCard(card);
                this._animateCountUp(card);
              }, i * 195);
            });
            gridObserver.disconnect();
          }
        }, { threshold: 0.6 });
        gridObserver.observe(grid);
      }

      if (carouselCards.length > 0) {
        this._animatedSlides = new Set();
        const carousel = this.querySelector('[data-mobile-carousel]');
        if (carousel) {
          const obs = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
              this._animateCarouselSlide(0);
              obs.disconnect();
            }
          }, { threshold: 0.2 });
          obs.observe(carousel);
        }
      }
    }

    _animateCardIn(card) {
      card.animate([
        { opacity: 0, transform: 'translateY(50px)' },
        { opacity: 1, transform: 'translateY(0px)' },
      ], {
        duration: 450,
        easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
        fill: 'forwards',
      });
    }

    _animateChartsInCard(card) {
      const donuts = [...card.querySelectorAll('[data-donut]')];
      donuts.forEach((el, i) => {
        const pieFill = el.querySelector('[data-pie-fill]');
        const pieNoise = el.querySelector('[data-pie-noise]');
        if (!pieFill) return;

        const pct = parseFloat(el.dataset.percentage) || 0;
        const targetAngle = (pct / 100) * 360;
        const isDonut = pieNoise && pieNoise.dataset.isDonut === 'true';
        const obj = { angle: 0 };

        gsap.to(obj, {
          angle: targetAngle,
          duration: 1.2,
          delay: 0.4 + i * 0.15,
          ease: 'power2.out',
          onUpdate() {
            pieFill.style.background = `conic-gradient(from 0deg, #A8E06C ${obj.angle}deg, transparent ${obj.angle}deg)`;
            if (pieNoise) {
              const radial = isDonut
                ? 'radial-gradient(circle, transparent 18%, black 28%, black 48%, transparent 62%)'
                : 'radial-gradient(circle, black 55%, transparent 72%)';
              const fadeEnd = Math.max(0, obj.angle - 8);
              const conic = `conic-gradient(from 0deg, transparent 0deg, black 10deg, black ${fadeEnd}deg, transparent ${obj.angle + 2}deg)`;
              pieNoise.style.webkitMaskImage = `${radial}, ${conic}`;
              pieNoise.style.webkitMaskComposite = 'source-in';
              pieNoise.style.maskImage = `${radial}, ${conic}`;
              pieNoise.style.maskComposite = 'intersect';
            }
          },
        });
      });

      const bars = [...card.querySelectorAll('[data-bar]')];
      bars.forEach((el, i) => {
        const barFill = el.querySelector('[data-bar-fill]');
        const barGradient = el.querySelector('[data-bar-gradient]');
        const barNoise = el.querySelector('[data-bar-noise]');
        if (!barFill) return;

        const pct = parseFloat(el.dataset.percentage) || 0;
        const obj = { height: 0 };

        gsap.to(obj, {
          height: pct * 0.85,
          duration: 1.2,
          delay: 0.4 + i * 0.15,
          ease: 'power2.out',
          onUpdate() {
            const h = `${obj.height}%`;
            barFill.style.height = h;
            if (barGradient) barGradient.style.height = h;
            if (barNoise) {
              barNoise.style.height = h;
              const fadeEnd = Math.max(0, obj.height - 8);
              const mask = `linear-gradient(to top, black 0%, black ${fadeEnd}%, transparent ${obj.height}%)`;
              barNoise.style.webkitMaskImage = mask;
              barNoise.style.maskImage = mask;
            }
          },
        });
      });
    }

    _initCarousel() {
      const gap = 8;
      const inset = 8;
      this.trackInset = inset;
      this.slidePositions = [];
      let x = 0;
      this.slides.forEach((slide) => {
        this.slidePositions.push(x);
        x += slide.offsetWidth + gap;
      });

      const carousel = this.querySelector('[data-mobile-carousel]');
      if (carousel && this.slideCount > 1) {
        const totalTrackWidth = x - gap;
        const maxScroll = inset + totalTrackWidth - carousel.offsetWidth + inset;
        const lastIdx = this.slideCount - 1;
        if (this.slidePositions[lastIdx] > maxScroll) {
          this.slidePositions[lastIdx] = maxScroll;
        }
      }

      gsap.set(this.track, { x: inset });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._goTo(this.current - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._goTo(this.current + 1));
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this._goTo(parseInt(dot.dataset.carouselDot)));
      });

      if (carousel) {
        let startX = 0, startY = 0, axis = null;
        carousel.addEventListener('touchstart', (e) => {
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
          axis = null;
        }, { passive: true });
        carousel.addEventListener('touchmove', (e) => {
          if (axis) return;
          const dx = Math.abs(e.changedTouches[0].clientX - startX);
          const dy = Math.abs(e.changedTouches[0].clientY - startY);
          if (dx + dy > 20) axis = dx > dy * 2 ? 'x' : 'y';
        }, { passive: true });
        carousel.addEventListener('touchend', (e) => {
          if (axis !== 'x') return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 40) this._goTo(this.current + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }
    }

    _goTo(index) {
      if (this.animating) return;
      if (index < 0) index = 0;
      if (index >= this.slideCount) index = this.slideCount - 1;
      if (index === this.current) return;
      this.animating = true;

      gsap.to(this.track, {
        x: this.trackInset - this.slidePositions[index],
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => { this.animating = false; },
      });

      this.current = index;
      this._animateCarouselDots();
      this._animateCarouselSlide(index);
    }

    _animateCarouselSlide(index) {
      if (!this._animatedSlides || this._animatedSlides.has(index)) return;
      this._animatedSlides.add(index);
      const slide = this.slides[index];
      if (!slide) return;
      const cards = [...slide.querySelectorAll('[data-stat-card]')];
      cards.forEach((card) => {
        this._animateChartsInCard(card);
        this._animateCountUp(card);
      });
    }

    _animateCountUp(card) {
      const els = [...card.querySelectorAll('[data-countup]')];
      els.forEach((el) => {
        const raw = el.dataset.countup;
        const match = raw.match(/^(\d+)(.*)/);
        if (!match) return;
        const target = parseInt(match[1], 10);
        const suffix = match[2] || '';
        const obj = { val: 0 };
        el.textContent = '0' + suffix;
        gsap.to(obj, {
          val: target,
          duration: 1.4,
          delay: 0.3,
          ease: 'power2.out',
          onUpdate() {
            el.textContent = Math.round(obj.val) + suffix;
          },
        });
      });
    }

    _animateCarouselDots() {
      this.dots.forEach((dot, i) => {
        const isActive = i === this.current;
        gsap.to(dot, {
          width: isActive ? 20 : 4,
          backgroundColor: isActive ? '#00403A' : 'rgba(0, 64, 58, 0.4)',
          duration: 0.35,
          ease: 'power2.out',
        });
      });
    }
  }

  customElements.define('proof-numbers', ProofNumbers);
}
